import { initGetApi } from './api';
import { callFilterRequest } from './api/filter.js';
import { handleBulkQuantity } from './bulk-quantity';
import { addToCart, handleCart } from './cart/index.js';
import { renderCollectionHeader } from './collection-header/index.js';
import { COLLECTION_ALL_STORAGE_KEY, FILTER_TREE_STATE_KEY } from './constants/filter-tree.js';
import { KEYS } from './constants/keys.js';
import { LIMIT, LIMIT_SETTING } from './constants/pagination.js';
import { WATERMARK_POWERED_BY_BOOST_URL } from './constants/url.js';
import {
  handleClickEmbeddedBundle,
  initDynamicBundle,
  renderPredictiveBlocksForProductPage,
} from './dynamicBundle/index.js';
import { handleDocumentClick } from './filter-tree/events/action-on-click';
import {
  applyFilterOption,
  closeFilterOptionHorizontalOpening,
  fillFilterTree,
  getFilterSettings,
  handleFilterTree,
  initCurrencyForFilterPrice,
  initFilterOptionCollapse,
  parseCollectionPath,
  parseHistoryParams,
} from './filter-tree/index.js';
import { handleInCollectionSearch } from './in-collection-search/index.js';
import {
  handleInstantSearchWidget,
  requestInstantSearchWidgetTemplate,
} from './instant-search-widget/index.js';
import { handleRecentSearchForSuggestionTerm } from './instant-search-widget/recent-search.js';
import { appIntegration } from './integration/index.js';
import { renderPreOrderBlocksForProductPage } from './pre-order';
import {
  closeQuickAddToCart,
  handleClickOutsideMiniPopup,
  handleClickProductItem,
  handleProductSwatches,
  quickAddToCart,
  setUniformProductImageHeight,
  transformProductPrice,
} from './product-item/index.js';
import { quickView } from './product-item/quick-view';
import {
  handleExceededPage,
  handlePaginationInfiniteScroll,
  handleProductListResponsive,
  scrollRestoration,
} from './product-list/index.js';
import { handleSearchResultTabs } from './search/index.js';
import { initState } from './state/index.js';
import { translate } from './template/templateRender';
import {
  handleClickOutsideLimitList,
  handleLimitListOptionClick,
  handleToggleShowLimitList,
} from './toolbar/limit-list.js';
import { handleClickOutsideSort, handleSort } from './toolbar/sort.js';
import { handleViewAsClick } from './toolbar/view-as.js';
import { handleTooltip } from './tooltip/index.js';
import {
  checkExistFilterOptionParam,
  debounce,
  formatMoney,
  getLocalStorage,
  getQueryParamByKey,
  getThemeSettings,
  handleClickTagAItemBuildUrlWithLocale,
  handleFirstLoadAfterRender,
  initDom,
  isProductPage,
  isSearchPage,
  isTabletPortraitMaxWidth,
  requestIdleCallback,
  safeParseJSON,
  scrollToTop,
  setLocalStorage,
  setPaginationSession,
  simpleMd5,
  watchFirstLoad,
} from './utils';
import { handleClickVolumeBundle, initVolumeBundle } from './volume-bundle/events/bundle-events';
import { startSession } from './workers/workers-provider.js';

let volumeBundleModule = null;
const loadVolumeBundleModule = async () => {
  if (!volumeBundleModule) {
    volumeBundleModule = await import(
      /* webpackChunkName: 'volume-bundle' */
      './volume-bundle'
    );
  }
  return volumeBundleModule;
};

export function initApp(context) {
  /**
   * Renders a template with data and includes translations from the application context.
   * @param {string} template - The template to render.
   * @param {Record<string, any>} data - The data to pass to the template.
   * @returns {string} - The rendered template.
   */
  context.templateRender = async function (template, data) {
    /**
     * The translations available in the application context.
     * @type {Record<string, any>}
     */
    const translations = context?.app?.translation || {};

    const { html } = await startSession('liquid-renderer').send({
      template,
      data: {
        ...data,
        translations,
      },
    });

    return html;

    // return templateRender(template, { ...data, translations });
  };
  /**
   * Translates a key using translations from the application context.
   * @param {string | number} key - The key to translate.
   * @param {string} defaultLabel - The default label to use if translation is not found.
   * @returns {string} - The translated label.
   */
  context.translate = function (key, defaultLabel) {
    /**
     * The translations available in the application context.
     * @type {Record<string, any>}
     */
    const translations = context?.app?.translation || {};
    return translate(key, translations, defaultLabel);
  };
  /**
   * init general
   * Wraps the provided callback function to handle customization logic.
   * Checks if custom handler functions are defined for the callback name
   * and block type, and wraps the original callback to call them before/after.
   * Allows overriding default handlers or injecting logic before/after calls.
   */
  context.handler = function (callBack, handlerName) {
    return async (...args) => {
      handlerName = !handlerName ? callBack.name : handlerName;
      const blockType = context.blockType;
      let fc = callBack;
      if (
        context.app.customization &&
        context.app.customization[handlerName] &&
        typeof context.app.customization[handlerName] === 'function'
      ) {
        fc = context.app.customization[handlerName];
      }

      if (
        context.app.customization &&
        context.app.customization[blockType]?.[handlerName] &&
        typeof context.app.customization[blockType]?.[handlerName] === 'function'
      ) {
        fc = context.app.customization[blockType][handlerName];
      }
      const capitalizedHandlerName = handlerName[0].toUpperCase() + handlerName.slice(1);
      const beforeHandlerName = `before${capitalizedHandlerName}`;
      const afterHandlerName = `after${capitalizedHandlerName}`;
      if (
        context.app.customization &&
        context.app.customization[beforeHandlerName] &&
        typeof context.app.customization[beforeHandlerName] === 'function'
      ) {
        await context.app.customization[beforeHandlerName](context, ...args);
      }
      if (
        context.app.customization &&
        context.app.customization[blockType]?.[beforeHandlerName] &&
        typeof context.app.customization[blockType][beforeHandlerName] === 'function'
      ) {
        await context.app.customization[blockType][beforeHandlerName](context, ...args);
      }
      const result = await fc(...args);
      if (
        context.app.customization &&
        context.app.customization[blockType]?.[handlerName]?.appIntegration &&
        typeof context.app.customization[blockType]?.[handlerName]?.appIntegration === 'function'
      ) {
        context.app.customization[blockType]?.[handlerName]?.appIntegration(...args);
      }

      if (
        context.app.customization &&
        context.app.customization[afterHandlerName] &&
        typeof context.app.customization[afterHandlerName] === 'function'
      ) {
        await context.app.customization[afterHandlerName](context, result, ...args);
      }
      if (
        context.app.customization &&
        context.app.customization[blockType]?.[afterHandlerName] &&
        typeof context.app.customization[blockType][afterHandlerName] === 'function'
      ) {
        await context.app.customization[blockType][afterHandlerName](context, result, ...args);
      }
      return result;
    };
  };

  /**
   * Renders the widget by executing the provided callback function.
   * Stores the callback in a map by hash of function string to allow
   * re-rendering when dependencies change.
   */
  context.render = function (callback, dependencies) {
    if (!dependencies) dependencies = ['latestFilterSearchRequest'];
    if (dependencies && Array.isArray(dependencies)) dependencies.push('latestFilterSearchRequest');
    if (!Array.isArray(dependencies)) throw new Error('dependencies must is a array');
    if (typeof callback === 'function') {
      callback(context);

      context.watchRender = context.watchRender || {};

      const fcName = simpleMd5(String(callback));
      if (!Object.keys(context.watchRender).includes(callback))
        context.watchRender[fcName] = {
          watchState: dependencies,
          key: fcName,
          callback,
        };
    }
  };

  initGetApi(context);
  initState(context);
  initDom(context);
  watchFirstLoad(context);
  handleLazyloadImage(context);

  context.app.customization = context.app.customization || {};
  context.app.customization[context.blockType] = context.app.customization[context.blockType] || {};
  context.app.customization[context.blockType].appIntegration = appIntegration;

  // Init block type filter
  if (context.blockType === 'filter') {
    // export context of filter to easy customize because context filter is dynamic
    window.filterContext = context;

    const { collection_id, collection_handle } =
      window.boostWidgetIntegration?.generalSettings || {};
    // save Id collection All to localStorage
    const getIdCollectionAll = getLocalStorage(COLLECTION_ALL_STORAGE_KEY);
    if (collection_handle === 'all' && getIdCollectionAll !== collection_id) {
      setLocalStorage(COLLECTION_ALL_STORAGE_KEY, collection_id);
    }

    context.document = document.getElementById(context.id);

    handleExceededPage(context);
    handleFilterTree(context);
    handleInCollectionSearch(context);

    context.render(() => {
      const robot = document.querySelector('meta[content="noindex,nofollow,nosnippet"]');
      const { enableRobot = true } = context.app?.generalSettings || {};
      const { isShortenUrlParam } = getFilterSettings(context);

      if (
        enableRobot &&
        !robot &&
        (checkExistFilterOptionParam(isShortenUrlParam) ||
          (getQueryParamByKey('q') && !isSearchPage(context)))
      ) {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex,nofollow,nosnippet';

        document.head.append(meta);
      }

      //bind unit format currency for range slider
      const _formatMoney = formatMoney(context);
      const units = context?.document?.querySelectorAll(
        '.boost-sd__filter-option-range-slider-unit'
      );

      if (units && units?.length > 0) {
        units?.forEach((unit) => {
          if (unit) unit.innerHTML = _formatMoney;
        });
      }

      const { paginationType = 'default' } =
        getThemeSettings(context)?.additionalElements?.pagination || {};

      // Need Remove animate class to position fixed is working.
      const animateNeedRemove = document.querySelectorAll(
        '.animate--slide-in, .animation--fade-in'
      );
      if (animateNeedRemove.length > 0) {
        animateNeedRemove.forEach((ele) => {
          ele.classList?.remove('animate--slide-in');
          ele.classList?.remove('animation--fade-in');
        });
      }

      initFilterOptionCollapse(context);
      initCurrencyForFilterPrice(context);
      // Product list
      scrollRestoration(context);
      handleTooltip(context);
      transformProductPrice(context);
      handleProductSwatches(context);
      handleProductListResponsive(context);
      setUniformProductImageHeight(
        context,
        context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnMobile || 2,
        context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnDesktop || 3,
        context.app.generalSettings?.breakpointmobile || 575,
        context.app.generalSettings?.breakpointtabletportraitmax || 991,
        3
      );
      if (
        context.app.templateSettings?.themeSettings?.productItems?.general?.subLayout ===
        'subLayout_bulk'
      ) {
        handleBulkQuantity(context);
      }
      if (isSearchPage(context)) {
        handleSearchResultTabs(context);
      }

      if (paginationType === 'infinite_scroll') {
        handlePaginationInfiniteScroll(context);
      }

      requestIdleCallback(function removeAnimation() {
        // Need Remove animate class to position fixed is working.
        const animateNeedRemove = document.querySelectorAll(
          '.animate--slide-in, .animation--fade-in'
        );
        if (animateNeedRemove.length > 0) {
          animateNeedRemove.forEach((ele) => {
            ele.classList?.remove('animate--slide-in');
            ele.classList?.remove('animation--fade-in');
          });
        }
      });

      const postProcesses = [
        () => handleProductListResponsive(context),
        () => handleProductSwatches(context),
        () => transformProductPrice(context),
        () => handleTooltip(context),
        () => scrollRestoration(context),
        () => initFilterOptionCollapse(context),
        () => initCurrencyForFilterPrice(context),
        () =>
          setUniformProductImageHeight(
            context,
            context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnMobile || 2,
            context.app.templateSettings?.themeSettings?.productList?.productsPerRowOnDesktop || 3,
            context.app.generalSettings?.breakpointmobile || 575,
            context.app.generalSettings?.breakpointtabletportraitmax || 991,
            3
          ),
      ];

      if (isTabletPortraitMaxWidth()) {
        postProcesses.forEach((postProcess) => requestAnimationFrame(postProcess));
      } else {
        postProcesses.forEach((postProcess) => postProcess());
      }
    }, ['filterTree']);

    handleHistoryParams(context);
    const page = Number(context?.defaultParams?.page) || 1;
    const productPerPage = getThemeSettings(context)?.productList?.productsPerPage || 16;
    const savedLimit = getLocalStorage(LIMIT);

    // Use saved limit if available and valid, otherwise use admin default
    const currentLimit =
      savedLimit && !isNaN(Number(savedLimit)) ? Number(savedLimit) : productPerPage;

    // Set the limit in context for API calls
    context.defaultParams = context.defaultParams || {};
    context.defaultParams.limit = currentLimit;

    setPaginationSession(page);

    // Save admin default for comparison
    setLocalStorage(LIMIT_SETTING, productPerPage);

    // Save current limit for persistence
    setLocalStorage(LIMIT, currentLimit);

    initDynamicBundle(context);
    initVolumeBundles(context);

    // NOTE: document listener, only use for click outside context.blockType == filter
    document.addEventListener('click', function (event) {
      if (!event || !context) return;
      const target = event.target;

      const { filterLayout } = getFilterSettings(context);

      //click outside filter option with horizontal style
      if (filterLayout === 'horizontal') {
        // click outside and current filter option is opening
        if (
          !target.closest('.boost-sd__filter-option') &&
          context.$('.boost-sd__filter-option-title--opening')
        ) {
          const [getItemSelecting] = context.useContextState('filterOptionItemSelecting', {});

          if (Object.keys(getItemSelecting()).length > 0) {
            applyFilterOption(context, {});
          } else {
            closeFilterOptionHorizontalOpening(context);
          }
        }
      }

      handleClickOutsideSort(context, event.target);
      handleClickOutsideLimitList(context, event.target);
      handleClickOutsideMiniPopup(event.target);
    });

    // General context.document filter listener
    context.document?.addEventListener('click', function (event) {
      handleDocumentClick(context, event);
    });

    // General context.document keydown listener
    context.document?.addEventListener('keydown', function (event) {
      const [, setFilterKeyCode] = context.useContextState(FILTER_TREE_STATE_KEY.KEY_CODE, '');
      const target = event.target;
      const currentButton = target.closest('button');
      const charCode = event?.which || event?.keyCode;
      if (currentButton && currentButton.id) {
        setFilterKeyCode(charCode);
      }

      if (KEYS.includes(event.key)) {
        // handle keydown on sortby
        if (
          target.classList &&
          [...target.classList].some((className) => className.startsWith('boost-sd__sorting-'))
        ) {
          context.handler(handleSort, 'handleSort')(context, target);
        }

        // handle keydown on viewas
        if (
          target.classList.contains('boost-sd__view-as-icon') ||
          target.closest('.boost-sd__view-as-icon')
        ) {
          context.handler(handleViewAsClick, 'handleViewAsClick')(context, target);
        }

        // handle keydown on limit-list
        if (target.classList.contains('boost-sd__show-limit-list-button')) {
          context.handler(handleToggleShowLimitList, 'handleToggleShowLimitList')(context, target);
        }
        if (target.classList.contains('boost-sd__show-limit-list-option')) {
          context.handler(handleLimitListOptionClick, 'handleLimitListOptionClick')(
            context,
            target
          );
        }

        //handle keydown on dynamic bundle as product
        if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
          handleClickEmbeddedBundle(context, target);
        }

        handleVolumeBundleClick(context, target);
      }
    });

    const debounceScrollToTopButton = debounce(function () {
      const btnScrollToTop = document.querySelector('.boost-sd__scroll-to-top');
      if (btnScrollToTop) {
        btnScrollToTop.style.display = window.scrollY > 300 ? 'block' : 'none';
      }
    }, 200);

    window.addEventListener('scroll', debounceScrollToTopButton);
  }

  if (context.blockType === 'instantSearch') {
    let init = false;

    if (isTabletPortraitMaxWidth()) {
      context.disableLazyInitBlock = true;
    }

    // Prefetch the template early if it does not exist
    if (!context.templateISW) {
      // Store the promise, do not await immediately
      context.templateISW = requestInstantSearchWidgetTemplate(context);
    }

    const initISWBlock = async () => {
      if (init) return;

      try {
        if (context.templateISW && typeof context.templateISW.then === 'function') {
          context.templateISW = await context.templateISW;
        }

        await context.handler(handleInstantSearchWidget, 'handleInstantSearchWidget')(context);
        init = true;

        document.removeEventListener('mousemove', initISWBlock);
        document.removeEventListener('touchmove', initISWBlock);
      } catch (err) {
        console.error('Failed to initialize Instant Search Widget:', err);
        document.addEventListener('mousemove', initISWBlock, { once: true });
        document.addEventListener('touchmove', initISWBlock, { once: true });
      }
    };

    if (context.disableLazyInitBlock) {
      initISWBlock();
    } else {
      document.addEventListener('mousemove', initISWBlock, { once: true });
      document.addEventListener('touchmove', initISWBlock, { once: true });
    }

    // register event re-init instant search for some case input Instant search render after some element is clicked
    window.addEventListener('bind-search-input', function (event) {
      console.log('Trigger bind search input', context);
      context.handler(handleInstantSearchWidget, 'handleInstantSearchWidget')(context);
    });

    context.document?.addEventListener('click', function (event) {
      const target = event.target;
      handleClickWatermark(target);

      handleRecentSearchForSuggestionTerm(event);

      if (target?.closest('.boost-sd__suggestion-queries-item--embedded-bundle')) {
        handleClickEmbeddedBundle(context, target);
      }

      if (target?.closest('.boost-sd__suggestion-queries-item--volume-bundle')) {
        handleClickVolumeBundle(context, target);
      }

      // handle productItem, Collections, SearchTerm ... in ISW build locale link
      const classTagALink =
        'a.boost-sd__suggestion-queries-link, a.boost-sd__suggestion-queries-item-link';
      // NOTE: always place latest for function handleClickTagAItemBuildUrlWithLocale. Because it have setWindowLocation lose event = null
      handleClickTagAItemBuildUrlWithLocale(event, classTagALink);
    });

    initDynamicBundle(context);
    initVolumeBundle(context);
    handleFirstLoadAfterRender(context);
  }

  if (context.blockType === 'recommendation') {
    // TODO: Get back to migrate these event after finising the refactoring of Cart and Filter modules
    context.document?.addEventListener('click', function (event) {
      const target = event.target;
      const metadataValue = target.dataset.metadata || target.getAttribute('metadata');
      const metadataObject = safeParseJSON(metadataValue);

      const handlers = {
        quickView: context.handler(quickView, 'quickView'),
        addToCart: context.handler(addToCart, 'addToCart'),
        quickAddToCart: context.handler(quickAddToCart, 'quickAddToCart'),
        goToProductDetail: context.handler(handleClickProductItem, 'handleClickProductItem'),
      };

      if (
        target.closest('.boost-sd__product-item, .boost-sd__product-item-list-view-layout') &&
        !target.closest('.boost-sd__popup-select-option') &&
        (!target.closest('.boost-sd__product-swatch') ||
          target.closest('.boost-sd__product-swatch-more')) &&
        !metadataObject &&
        !metadataObject?.action
      ) {
        context.handler(handleClickProductItem, 'handleClickProductItem')(context, null, target);
      }

      // action.optionCollection -> collection multi level - prevent default
      if (!target.getAttribute('href') || metadataObject.action?.optionCollection) {
        event.preventDefault();
      }

      if (metadataObject && metadataObject.action) {
        // event.preventDefault();
        Object.keys(metadataObject.action).forEach((key) => {
          handlers[key](context, metadataObject.action[key], target);
        });
      }
      const actionId = target.dataset.action || target.getAttribute('data-action');
      if (actionId) {
        const [getActionMapping] = context.useContextState('actionMapping', {});
        const actionMapping = getActionMapping();
        const [key, id] = actionId.split('.');

        if (actionMapping[key] && actionMapping[key][id]) {
          const actionHandler = Object.keys(actionMapping[key][id])[0];
          const action = actionMapping[key][id][actionHandler];
          handlers[actionHandler](context, action, target);
        }
      }

      if (target.closest('.boost-sd__popup-select-option-close-btn')) {
        closeQuickAddToCart(target);
      }

      handleClickWatermark(target);
    });
  }

  if (context.blockType === 'cart') {
    handleCart(context);
  }

  if (context.blockType === 'predictiveBundle' && isProductPage()) {
    context.app.customization[context.blockType].appIntegration(); // dispatchEvent afterRender Bundle
    const productId = window.boostWidgetIntegration?.generalSettings?.product_id;
    renderPredictiveBlocksForProductPage(context, productId);
    handleFirstLoadAfterRender(context);
  }

  if (context.blockType === 'volumeBundle' && isProductPage()) {
    context.app.customization[context.blockType].appIntegration(); // dispatchEvent afterRender Bundle
    loadVolumeBundleModule().then((module) => {
      const { renderVolumeBlocksForProductPage } = module;
      const productId = window.boostWidgetIntegration?.generalSettings?.product_id;
      renderVolumeBlocksForProductPage(context, productId);
      handleFirstLoadAfterRender(context);
    });
  }

  if (context.blockType === 'preOrder' && isProductPage()) {
    renderPreOrderBlocksForProductPage(context);
  }

  if (
    context.blockType === 'backInStock' &&
    context.app?.backInStock?.enabled === true &&
    context.app?.backInStock?.isTurbo === true &&
    isProductPage()
  ) {
    import(
      /* webpackChunkName: 'back-in-stock' */
      './back-in-stock'
    ).then((module) => {
      const { integrateBackInStockFeature } = module;

      integrateBackInStockFeature(context);
    });
  }
}

const loadVolumeBundle = (context, callbackName, ...args) => {
  if (context?.dynamicBundles?.length > 0) {
    context.dynamicBundles.forEach((bundle) => {
      if (bundle.bundle_type === 'volume') {
        loadVolumeBundleModule().then((module) => {
          const callback = module[callbackName];
          if (typeof callback === 'function') {
            callback(context, ...args);
          }
        });
      }
    });
  }
};

const handleVolumeBundleClick = (context, target) => {
  if (target?.closest('.boost-sd__product-item--volume-bundle')) {
    loadVolumeBundle(context, 'handleClickVolumeBundle', target);
  }
};

const initVolumeBundles = (context) => {
  loadVolumeBundle(context, 'initVolumeBundle');
};

const handleHistoryParams = (context) => {
  window.addEventListener('popstate', async () => {
    // back or forward
    parseHistoryParams(context);
    parseCollectionPath(context);
    callFilterRequest(context);
    fillFilterTree(context);
    scrollToTop({ behavior: 'auto' });
    renderCollectionHeader(context);
  });

  // first load
  parseHistoryParams(context);
  fillFilterTree(context);
};

const handleLazyloadImage = (context) => {
  const { lazyLoadImages } = window.boostSDTaeUtils;
  if (lazyLoadImages) {
    context.render(() => {
      lazyLoadImages(context.document);
    });
  }
};

const handleClickWatermark = (target) => {
  if (target?.closest('.boost-sd__watermark-powered-by-boost')) {
    window.open(WATERMARK_POWERED_BY_BOOST_URL, '_blank').focus();
  }
};
